import React, { useState } from 'react';
import { toast } from 'sonner';
import NavBar from '../components/NavBar';
import { useAuth } from '../lib/auth';
import { gethinhAPI, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Download, Link2, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const EXAMPLES = [
  { name: 'Freepik', url: 'https://www.freepik.com/free-vector/...' },
  { name: 'Shutterstock', url: 'https://www.shutterstock.com/image-photo/...' },
  { name: 'Envato Elements', url: 'https://elements.envato.com/...' },
  { name: 'Adobe Stock', url: 'https://stock.adobe.com/images/...' },
];

export default function SearchPage() {
  const { user, refreshUser } = useAuth();
  const [link, setLink] = useState('');
  const [quote, setQuote] = useState(null);
  const [preview, setPreview] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [dlLoading, setDlLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [result, setResult] = useState(null);

  const getQuote = async () => {
    if (!link.trim()) { toast.error('Lutfen bir link yapistirin'); return; }
    setCheckLoading(true);
    setResult(null);
    setPreview(null);
    try {
      const [quoteRes, previewRes] = await Promise.all([gethinhAPI.quote(link.trim()), gethinhAPI.preview(link.trim()).catch(() => null)]);
      setQuote(quoteRes.data);
      if (previewRes?.data) setPreview(previewRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCheckLoading(false);
    }
  };

  const doDownload = async () => {
    if (!link.trim()) return;
    setDlLoading(true);
    try {
      const { data } = await gethinhAPI.download(link.trim());
      setResult(data);
      toast.success(`Indirme hazirlandi! ${data.price_tl} TL dusuldu.`);
      refreshUser();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDlLoading(false);
    }
  };

  const fetchProxy = async () => {
    if (!result?.proxy_url) return;
    setFileLoading(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}${result.proxy_url}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Hata ${res.status}`);
      }
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const fname = match?.[1] || (result.item?.name ? `${result.item.name}` : `cihanstock-${result.download_id}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Dosya indirildi');
    } catch (e) {
      toast.error(e.message || 'Dosya indirilemedi');
    } finally {
      setFileLoading(false);
    }
  };

  const balance = user?.balance_tl || 0;
  const canAfford = quote ? balance >= quote.price_tl : true;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <NavBar />
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Premium Indirme</h1>
          <p className="text-muted-foreground">Shutterstock, Envato, Freepik ve daha fazlasindan linki yapistir, indirmeye basla.</p>
        </div>

        {/* Link input card */}
        <div className="glass-card rounded-2xl p-6 mb-6">
          <label className="text-sm font-medium mb-2 block">Saglayici linkini yapistir</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={link}
                onChange={(e) => { setLink(e.target.value); setQuote(null); setPreview(null); setResult(null); }}
                placeholder="https://www.shutterstock.com/image-photo/..."
                className="pl-9 bg-white/5 border-white/10"
                data-testid="search-link-input"
              />
            </div>
            <Button onClick={getQuote} disabled={checkLoading || !link.trim()} variant="outline" className="border-white/10" data-testid="search-check-btn">
              {checkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fiyat Kontrol'}
            </Button>
          </div>
          {preview && !result && (
            <div className="mt-4 p-4 rounded-xl bg-black/30 border border-white/10" data-testid="preview-panel">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">On izleme</p>
              <div className="flex items-center gap-4">
                <img src={preview.thumbnail || `${process.env.REACT_APP_BACKEND_URL}${preview.placeholder_url}`} alt="On izleme" className="w-32 h-20 object-cover rounded-lg border border-white/10" />
                <div>
                  <p className="font-semibold capitalize">{preview.provider === 'default' ? 'Diger saglayici' : preview.provider}</p>
                  <p className="text-xs text-muted-foreground">Gercek gorsel on izlemesi alinamazsa marka karti gosterilir.</p>
                </div>
              </div>
            </div>
          )}
          {/* Quote */}
          {quote && !result && (
            <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10" data-testid="quote-panel">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Tespit edilen saglayici</p>
                  <p className="text-lg font-semibold capitalize">{quote.provider === 'default' ? 'Diger' : quote.provider}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Indirme ucreti</p>
                  <p className="text-2xl font-bold text-emerald-400 tabular-nums">{quote.price_tl.toFixed(2)} TL</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs border-t border-white/10 pt-3">
                <span className="text-muted-foreground">Mevcut bakiye: <span className="font-semibold text-foreground">{balance.toFixed(2)} TL</span></span>
                {canAfford ? (
                  <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> Yeterli bakiye</span>
                ) : (
                  <span className="flex items-center gap-1 text-rose-400"><AlertCircle className="w-3.5 h-3.5" /> Yetersiz bakiye</span>
                )}
              </div>
              <Button
                onClick={doDownload}
                disabled={dlLoading || !canAfford}
                className="w-full mt-4 bg-gradient-to-r from-fuchsia-500 to-indigo-500 hover:from-fuchsia-600 hover:to-indigo-600"
                data-testid="search-download-btn"
              >
                {dlLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Indirme hazirlaniyor...</> : <><Download className="w-4 h-4 mr-2" /> Onayla ve Indir</>}
              </Button>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="mt-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30" data-testid="download-result">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <h3 className="font-semibold">Indirme Hazir!</h3>
              </div>
              {result.item?.thumbnail && (
                <img src={result.item.thumbnail} alt={result.item.name} className="w-full max-w-sm rounded-lg mb-3 border border-white/10" />
              )}
              <div className="space-y-1 text-sm mb-4">
                {result.item?.name && <p><span className="text-muted-foreground">Dosya:</span> {result.item.name}</p>}
                {result.item?.type && <p><span className="text-muted-foreground">Tip:</span> {result.item.type}</p>}
                {result.item?.size && <p><span className="text-muted-foreground">Boyut:</span> {result.item.size}</p>}
                <p><span className="text-muted-foreground">Ucret:</span> <span className="text-emerald-400 font-semibold">{result.price_tl.toFixed(2)} TL</span></p>
                <p><span className="text-muted-foreground">Yeni bakiye:</span> {result.new_balance_tl.toFixed(2)} TL</p>
              </div>
              <a href={result.download_link} target="_blank" rel="noopener noreferrer" data-testid="download-link-btn" className="hidden">_</a>
              <Button
                onClick={fetchProxy}
                disabled={fileLoading}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="download-file-btn"
              >
                {fileLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Dosya hazirlaniyor...</> : <><Download className="w-4 h-4 mr-2" /> Dosyayi Indir</>}
              </Button>
            </div>
          )}
        </div>

        {/* Examples */}
        <div className="glass-card rounded-2xl p-6">
          <h3 className="font-semibold mb-3">Desteklenen ornek linkler</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {EXAMPLES.map(e => (
              <div key={e.name} className="flex items-start gap-2 p-3 rounded-lg bg-white/5 border border-white/5 text-sm">
                <span className="font-semibold text-fuchsia-400 shrink-0">{e.name}:</span>
                <span className="text-muted-foreground truncate">{e.url}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">Shutterstock, Adobe Stock, Envato, iStock, Getty Images, Freepik, Vecteezy, Flaticon, Storyblocks, Motion Array, PNGTree, Lovepik, Creative Fabrica, 123RF, Dreamstime, Depositphotos ve daha fazlasi desteklenmektedir.</p>
        </div>
      </div>
    </div>
  );
}


